import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { generateClient } from 'aws-amplify/api';
import { getUrl, uploadData } from 'aws-amplify/storage'; 
import { getCurrentUser, fetchUserAttributes } from 'aws-amplify/auth'; 
import { getBook, listExchangeRates } from '../graphql/queries';
// QUIRÚRGICO: Añadida createNotification a las mutaciones
import { createBookOrder, createNotification } from '../graphql/mutations';
import { FiArrowLeft, FiUser, FiLock } from 'react-icons/fi'; 
import './ProductPage.css';

const ProductPage = () => {
    const { id } = useParams(); 
    const navigate = useNavigate();
    const [book, setBook] = useState(null);
    const [tasa, setTasa] = useState(1);
    const [loading, setLoading] = useState(true);
    const [imageUrl, setImageUrl] = useState(null);
    const [galleryUrls, setGalleryUrls] = useState([]);
    const [isLoggedIn, setIsLoggedIn] = useState(false); 

    const [format, setFormat] = useState(''); 
    const [receiptFile, setReceiptFile] = useState(null);
    const [pagoData, setPagoData] = useState({ nombre: '', cedula: '', banco: '', referencia: '' });

    useEffect(() => {
        const fetchData = async () => {
            const client = generateClient();
            try {
                try {
                    await getCurrentUser();
                    setIsLoggedIn(true);
                } catch {
                    setIsLoggedIn(false);
                }

                const resTasa = await client.graphql({ 
                    query: listExchangeRates, 
                    authMode: 'apiKey' 
                });
                setTasa(resTasa.data.listExchangeRates.items[0]?.value || 1);

                const result = await client.graphql({ 
                    query: getBook, 
                    variables: { id }, 
                    authMode: 'apiKey' 
                });
                const bookData = result.data.getBook;
                setBook(bookData);

                if (bookData.hasOnline) setFormat('ONLINE');
                else if (bookData.hasDigital) setFormat('PDF');
                else if (bookData.hasPhysical) setFormat('PHYSICAL');

                let allImages = [];
                if (bookData.imageKey) {
                    const link = await getUrl({ key: bookData.imageKey });
                    setImageUrl(link.url);
                    allImages.push(link.url);
                }

                if (bookData.images && bookData.images.length > 0) {
                    const extraUrls = await Promise.all(bookData.images.map(async (key) => {
                        try { 
                            const res = await getUrl({ key }); 
                            return res.url; 
                        } catch (err) { 
                            return null; 
                        }
                    }));
                    allImages = [...allImages, ...extraUrls.filter(u => u !== null)];
                }
                setGalleryUrls(allImages);

            } catch (error) { 
                console.error("Error cargando producto:", error); 
            } finally { 
                setLoading(false); 
            }
        };
        fetchData();
    }, [id]);

    const getPriceUSD = () => {
        if (!book) return 0;
        if (format === 'PHYSICAL') return parseFloat(book.pricePhysical || book.priceUSD).toFixed(2);
        if (format === 'PDF') return parseFloat(book.pricePDF || book.priceUSD).toFixed(2);
        if (format === 'ONLINE') return parseFloat(book.priceOnline || book.priceUSD).toFixed(2);
        return parseFloat(book.priceUSD).toFixed(2);
    };

    const handleLoginRedirect = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        alert("Por favor, utilice el botón 'Ingresar' en la parte superior derecha para iniciar sesión o registrarse.");
    };

    const handleNotificarPago = async (e) => {
        e.preventDefault();
        
        let user;
        try {
            user = await getCurrentUser();
        } catch (authError) {
            alert("⚠️ ACCESO RESTRINGIDO: Debe iniciar sesión en el menú superior para reportar un pago.");
            handleLoginRedirect();
            return;
        }

        const client = generateClient();
        setLoading(true);

        try {
            const attributes = await fetchUserAttributes();
            const activeEmail = attributes.email || user.username;

            if (!receiptFile) {
                alert("⚠️ Por favor, selecciona la foto del comprobante.");
                setLoading(false);
                return;
            }

            const fileExtension = receiptFile.name.split('.').pop();
            const cleanEmail = activeEmail.replace(/[^a-zA-Z0-9]/g, "_");
            const fileName = `receipts/${Date.now()}-${cleanEmail}.${fileExtension}`;
            
            await uploadData({ 
                path: `public/${fileName}`, 
                data: receiptFile,
                options: { contentType: receiptFile.type }
            }).result;

            const orderInput = {   
                bookID: id, 
                customerName: pagoData.nombre,
                customerEmail: activeEmail,
                idNumber: pagoData.cedula || "N/A",
                bankName: pagoData.banco || "Pago Movil",
                referenceNumber: pagoData.referencia,
                bookTitle: book.title,
                amountUSD: parseFloat(getPriceUSD()),
                amountBS: parseFloat((getPriceUSD() * tasa).toFixed(2)),
                receiptKey: fileName, 
                category: format,
                status: "PENDIENTE",
                orderDate: new Date().toISOString()
            };

            await client.graphql({ 
                query: createBookOrder, 
                variables: { input: orderInput }, 
                authMode: 'userPool',
            });

            // INYECCIÓN QUIRÚRGICA: Notificar al Dr. Giovanni sobre la venta del libro
            await client.graphql({
                query: createNotification,
                variables: {
                    input: {
                        recipientEmail: "giovanniscarvaciyasociados@gmail.com",
                        title: "📚 NUEVA VENTA DE LIBRO",
                        message: `El cliente ${activeEmail} reportó pago para el libro: ${book.title}. Formato: ${format}. Ref: ${pagoData.referencia}`,
                        isRead: false
                    }
                },
                authMode: 'userPool'
            });

            alert("✅ ¡PAGO REPORTADO CON ÉXITO! Revisaremos su comprobante pronto.");
            setPagoData({ nombre: '', cedula: '', banco: '', referencia: '' });
            setReceiptFile(null);
            
        } catch (error) { 
            console.error("DETALLE DEL ERROR:", error);
            const errorMsg = error.errors ? error.errors[0].message : error.message;
            alert("❌ " + errorMsg); 
        } finally { 
            setLoading(false); 
        }
    };

    if (loading) return <div className="loader">Cargando obra...</div>;
    if (!book) return <div>Libro no encontrado.</div>;

    const precioBs = (getPriceUSD() * tasa).toFixed(2);

    return (
        <div className="amazon-product-container">
            <div className="back-nav" style={{ marginBottom: '20px' }}>
                <button onClick={() => navigate('/libros')} style={{ background: 'none', border: 'none', color: '#007185', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', fontWeight: 'bold' }}>
                    <FiArrowLeft /> Volver al catálogo
                </button>
            </div>

            <div className="product-main-section">
                <div className="product-image-column">
                    <div className="main-image-card">
                        <img src={imageUrl || "https://via.placeholder.com/300x400"} alt={book.title} className="main-book-img" />
                    </div>
                    {galleryUrls.length > 1 && (
                        <div className="gallery-container">
                            {galleryUrls.map((url, index) => (
                                <div key={index} className={`thumb-wrapper ${imageUrl === url ? 'active-thumb' : ''}`} onClick={() => setImageUrl(url)}>
                                    <img src={url} alt={`Vista ${index}`} />
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="product-info-column">
                    <h1 className="product-title">{book.title}</h1>
                    <p className="author-tag">por <FiUser /> <strong>{book.author || "Dr. Giovanni Scarvaci"}</strong></p>
                    <hr />
                    <div className="description-section">
                        <h3>Detalles del Producto:</h3>
                        <div className="indice-text" style={{ whiteSpace: 'pre-line' }}>{book.description}</div>
                    </div>
                </div>

                <div className="product-buy-column">
                    <div className="buy-card">
                        <div className="price-tag">
                            <span className="usd-big">${getPriceUSD()}</span>
                            <p className="bs-small">({precioBs} Bs.)</p>
                        </div>

                        <div className="selector-formato" style={{margin: '15px 0'}}>
                            <label style={{fontSize: '12px', fontWeight: 'bold'}}>Seleccione Modalidad:</label>
                            <select value={format} onChange={(e) => setFormat(e.target.value)} className="pro-input" style={{width: '100%', marginTop: '5px', padding: '8px'}}>
                                {book.hasOnline && <option value="ONLINE">📖 Lectura Digital Web (${parseFloat(book.priceOnline).toFixed(2)})</option>}
                                {book.hasDigital && <option value="PDF">📥 Descarga PDF (${parseFloat(book.pricePDF).toFixed(2)})</option>}
                                {book.hasPhysical && <option value="PHYSICAL">📦 Libro Físico (${parseFloat(book.pricePhysical).toFixed(2)})</option>}
                            </select>
                        </div>

                        <div className="metodos-pago">
                            <h4>Notificar Pago Móvil</h4>
                            <div className="pago-movil-datos" style={{fontSize: '11px', background: '#f9f9f9', padding: '10px', borderRadius: '5px', border: '1px solid #eee', marginBottom: '15px'}}>
                                <p><strong>Banco:</strong> 0102 (BDV) | <strong>Cédula:</strong> V-16.129.666</p>
                                <p><strong>Teléfono:</strong> 0424-300-9284</p>
                            </div>

                            {!isLoggedIn ? (
                                <div style={{ textAlign: 'center', padding: '20px', backgroundColor: '#fff3cd', borderRadius: '8px', border: '1px solid #ffeeba' }}>
                                    <FiLock size="30px" color="#856404" />
                                    <p style={{ fontSize: '12px', fontWeight: 'bold', color: '#856404', marginTop: '10px' }}>
                                        Debe estar registrado para reportar un pago.
                                    </p>
                                    <button onClick={handleLoginRedirect} className="btn-notificar" style={{ marginTop: '10px', backgroundColor: '#001f3f' }}>
                                        IR AL MENÚ DE INGRESO
                                    </button>
                                </div>
                            ) : (
                                <form onSubmit={handleNotificarPago} className="formulario-confirmacion">
                                    <input type="text" placeholder="Nombre de quien pagó" required value={pagoData.nombre} onChange={e => setPagoData({...pagoData, nombre: e.target.value})} />
                                    <input type="text" placeholder="Nro de Referencia" required value={pagoData.referencia} onChange={e => setPagoData({...pagoData, referencia: e.target.value})} />
                                    <div style={{marginTop: '10px'}}>
                                        <label style={{fontSize: '10px', display: 'block', marginBottom: '5px'}}>📸 Adjuntar Comprobante (Capture):</label>
                                        <input type="file" accept="image/*" required onChange={e => setReceiptFile(e.target.files[0])} />
                                    </div>
                                    <button type="submit" className="btn-notificar" disabled={loading} style={{marginTop: '15px'}}>
                                        {loading ? "PROCESANDO..." : "ENVIAR COMPROBANTE"}
                                    </button>
                                </form>
                            )}
                            
                            <hr style={{margin: '20px 0'}} />
                            <button className="btn-zelle-wa" onClick={() => window.open(`https://wa.me/14078738172?text=Deseo pagar ${book.title} en formato ${format}`, '_blank')}>Pagar con Zelle / Otros</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductPage;